import React, { useEffect, useState } from 'react';
import { toolsAPI } from '../api';
import {
  X, Settings, Briefcase, GraduationCap, PenLine, Microscope, Handshake, Users,
  Code2, ShieldCheck, BarChart3, DollarSign, Tag, AlertTriangle, AlertOctagon,
  Presentation, LineChart, Image as ImageIcon, Loader2, Sparkles, Globe2
} from 'lucide-react';

// lucide icons keyed by the `icon` string returned from the backend catalogue. Kept
// inline here (rather than dynamic-import) so the bundler tree-shakes only what's used.
const ICON_MAP = {
  'settings': Settings, 'briefcase': Briefcase, 'graduation-cap': GraduationCap,
  'pen-line': PenLine, 'microscope': Microscope, 'handshake': Handshake,
  'users': Users, 'code-2': Code2, 'shield-check': ShieldCheck,
  'bar-chart-3': BarChart3, 'dollar-sign': DollarSign, 'tag': Tag,
  'alert-triangle': AlertTriangle, 'alert-octagon': AlertOctagon,
  'presentation': Presentation, 'line-chart': LineChart, 'image': ImageIcon,
};

// Languages list mirrors the workspace settings dropdown so users get the same options
// wherever they tweak language. Sorted alphabetically (English first as common default).
const LANGUAGES = [
  'English', 'Arabic', 'Chinese (Simplified)', 'Dutch', 'French', 'German',
  'Hindi', 'Italian', 'Japanese', 'Korean', 'Portuguese', 'Russian', 'Spanish', 'Turkish',
];

export default function ReportBuilderModal({ workspaceId, defaultLanguage, onClose, onRun }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [language, setLanguage] = useState(defaultLanguage || 'English');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    toolsAPI.reportTypes().then(r => {
      if (!mounted) return;
      setTypes(r.data?.report_types || []);
    }).catch(e => console.error(e)).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const handleRun = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      // Delegate the actual run to the parent so the result feeds back into the same
      // Tools panel "cached results" pipeline as a regular run.
      await onRun({
        report_type: selected.id,
        report_language: language,
        report_description: description.trim() || null,
      });
      onClose();
    } catch (e) {
      console.error('Report generation failed:', e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="report-builder-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#F59E0B]/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#D97706]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>
                ReportBuilder
              </h2>
              <p className="text-xs text-[#6B7280]">Choose a report type, language, and describe how you want it</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#F3F4F6] rounded-xl transition-colors"
            data-testid="report-builder-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-[#6B7280]" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 space-y-6">
          {/* Report type grid */}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-3 block">
              Report type {selected && <span className="ml-2 text-[#6590FF] normal-case font-medium">— {selected.name}</span>}
            </label>
            {loading ? (
              <div className="flex items-center justify-center h-32 text-[#9CA3AF]">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {types.map(t => {
                  const Icon = ICON_MAP[t.icon] || Settings;
                  const isSelected = selected?.id === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setSelected(t)}
                      className={`text-left p-3 rounded-2xl border-2 transition-all hover:shadow-md group ${
                        isSelected
                          ? 'border-[#6590FF] bg-[#F8FAFF] shadow-md'
                          : 'border-[#E5E7EB] bg-white hover:border-[#D1D5DB]'
                      }`}
                      data-testid={`report-type-${t.id}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${t.color}15` }}
                        >
                          <Icon className="w-4 h-4" style={{ color: t.color }} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-[#111827] leading-snug">{t.name}</div>
                          <div className="text-[11px] text-[#6B7280] leading-relaxed mt-0.5 line-clamp-2">{t.desc}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Language */}
          <div>
            <label htmlFor="rb-language" className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 flex items-center gap-2">
              <Globe2 className="w-3.5 h-3.5" /> Language
            </label>
            <select
              id="rb-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full h-11 px-3 border border-[#E5E7EB] rounded-xl text-sm bg-white focus:outline-none focus:border-[#6590FF] focus:ring-2 focus:ring-[#6590FF]/20"
              data-testid="report-language-select"
            >
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="rb-description" className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2 block">
              Description / instructions <span className="text-[#9CA3AF] font-normal normal-case">(optional)</span>
            </label>
            <textarea
              id="rb-description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                selected?.id === 'custom'
                  ? 'Describe how you want this report: structure, sections, tone, audience, anything the AI should know.'
                  : 'Optionally describe what to focus on, sections to emphasise, things to ignore, etc.'
              }
              className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-xl text-sm resize-none focus:outline-none focus:border-[#6590FF] focus:ring-2 focus:ring-[#6590FF]/20"
              data-testid="report-description-input"
            />
            <p className="text-[11px] text-[#9CA3AF] mt-1.5">
              These instructions are passed to the AI alongside your selected report type.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#E5E7EB] bg-[#FAFAFB] flex-shrink-0">
          <p className="text-xs text-[#6B7280]">
            {selected
              ? <>Will generate a <strong className="text-[#111827]">{selected.name}</strong> in <strong className="text-[#111827]">{language}</strong></>
              : <>Select a report type to continue</>}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 h-10 rounded-xl border border-[#E5E7EB] bg-white text-sm font-medium text-[#4B5563] hover:bg-[#F3F4F6] transition-colors"
              data-testid="report-builder-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleRun}
              disabled={!selected || submitting}
              className="px-5 h-10 rounded-xl bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="report-builder-generate-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
